import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle,
  AlertCircle,
  Crown,
  Zap,
  Building,
  Users,
  MessageSquare,
  BarChart3,
  Loader2,
  CreditCard,
  ArrowLeft,
  Lock,
  FileText,
  UtensilsCrossed,
  GraduationCap,
  Handshake,
  Heart,
  Book,
  Dumbbell,
  Instagram,
  Linkedin,
  Youtube,
  Search,
  Store,
  ShoppingCart,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { showApiError, friendlyMessage } from '@/lib/errors';
import { subscriptionsApi, SubscriptionPlan, ApiError } from '@/lib/api';
import { awaitSubscriptionAccess } from '@/lib/billing/awaitSubscriptionAccess';
import { useAuth } from '@/components/AuthProvider';
import { isTrialActuallyExpired as checkTrialExpired, isSubscriptionActive, isSubscriptionExpiredOrCancelled } from '@/lib/subscription-utils';

declare global {
  interface Window {
    Razorpay: any;
    Cashfree: any;
  }
}

const loadRazorpayScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout script'));
    document.body.appendChild(script);
  });
};

const loadCashfreeScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Cashfree checkout script'));
    document.body.appendChild(script);
  });
};

const formatPlanFeatures = (features: any): string[] => {
  if (!features) return [];
  if (Array.isArray(features)) return features;
  if (features.features && Array.isArray(features.features)) return features.features;
  return [];
};

const getPlanColorScheme = (sortOrder: number) => {
  switch (sortOrder) {
    case 0: // Basic/Starter Plan
      return {
        accentColor: '#0F172A',
        accentGradient: 'linear-gradient(135deg, #0F172A 0%, #334155 100%)',
        borderColor: '#E2E8F0',
        iconBg: 'var(--third-color)',
        iconColor: 'var(--secondary-color)',
        buttonBg: '#FFFFFF',
        buttonTextColor: '#0F172A',
        buttonBorder: '1px solid #E2E8F0',
        buttonHover: '#F8FAFC',
        shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        cardBg: '#FFFFFF',
        accentGlow: 'rgba(226, 232, 240, 0.3)',
      };
    case 1: // Most Popular Plan
      return {
        accentColor: 'var(--secondary-color)',
        accentGradient: 'var(--main-color)',
        borderColor: 'var(--secondary-color)',
        iconBg: 'var(--third-color)',
        iconColor: 'var(--secondary-color)',
        buttonBg: 'var(--main-color)',
        buttonTextColor: '#FFFFFF',
        buttonBorder: 'none',
        buttonHover: 'var(--secondary-color)',
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        cardBg: '#FFFFFF',
        accentGlow: 'rgba(26, 177, 105, 0.1)',
      };
    case 2: // Enterprise Plan
      return {
        accentColor: '#6366F1',
        accentGradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
        borderColor: '#6366F1',
        iconBg: '#EEF2FF',
        iconColor: '#6366F1',
        buttonBg: '#6366F1',
        buttonTextColor: '#FFFFFF',
        buttonBorder: 'none',
        buttonHover: '#4F46E5',
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        cardBg: '#FFFFFF',
        accentGlow: 'rgba(99, 102, 241, 0.1)',
      };
    default:
      return {
        accentColor: '#64748B',
        accentGradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)',
        borderColor: '#E2E8F0',
        iconBg: '#F1F5F9',
        iconColor: '#475569',
        buttonBg: '#FFFFFF',
        buttonTextColor: '#64748B',
        buttonBorder: '1px solid #E2E8F0',
        buttonHover: '#F1F5F9',
        shadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        cardBg: '#FFFFFF',
        accentGlow: 'rgba(0, 0, 0, 0.05)',
      };
  }
};

const formatLimit = (limit: number | null | undefined, isEnabled: boolean = true) => {
  if (!isEnabled) return 'Not Included';
  if (limit === null || limit === undefined) return '0';
  if (limit === -1) return 'Unlimited';
  if (limit === 0) return '0';
  if (limit >= 1000) return `${limit / 1000}K`;
  return limit.toString();
};

const formatFeatureName = (key: string): string => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getAllFeatures = (features: any): Array<{ key: string; name: string; enabled: boolean; limit: number | null; subFeatures?: any }> => {
  if (!features || typeof features !== 'object') return [];

  if (features.features && Array.isArray(features.features) && features.limits) {
    return [];
  }

  const featureList: Array<{ key: string; name: string; enabled: boolean; limit: number | null; subFeatures?: any }> = [];

  const featureOrder = [
    'inbox',
    'contacts',
    'templates',
    'campaigns',
    'whatsapp_flows',
    'automations',
    'business_accounts',
    'analytics',
    'commerce',
    'integrations',
    'team_management',
    'messages_per_month',
    'ai_generation',
  ];

  const featureDisplayNames: Record<string, string> = {
    inbox: 'Inbox',
    contacts: 'Contacts',
    templates: 'Templates',
    campaigns: 'Campaigns',
    whatsapp_flows: 'WhatsApp Flows',
    automations: 'Automations',
    business_accounts: 'Business Accounts',
    analytics: 'Analytics',
    commerce: 'Commerce',
    integrations: 'Integrations',
    team_management: 'Team Management',
    messages_per_month: 'Messages per Month',
    ai_generation: 'AI Generation',
  };

  featureOrder.forEach(key => {
    if (features[key] && typeof features[key] === 'object') {
      const feature = features[key];
      featureList.push({
        key,
        name: featureDisplayNames[key] || formatFeatureName(key),
        enabled: feature.enabled !== false,
        limit: feature.limit ?? null,
        subFeatures: feature.sub_features,
      });
    }
  });

  return featureList;
};

interface PlanCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan: boolean;
  planFeatures: string[];
  planPrice: number;
  sortOrder: number;
  colorScheme: ReturnType<typeof getPlanColorScheme>;
  isYearly: boolean;
  onSelectPlan: (plan: SubscriptionPlan) => void;
  isFirstTwo: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isCurrentPlan,
  planFeatures,
  planPrice,
  sortOrder,
  colorScheme,
  isYearly,
  onSelectPlan,
  isFirstTwo,
}) => {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const cardRef = useRef<HTMLDivElement>(null);
  const rippleIdRef = useRef(0);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -5; // Max 5 degrees
    const rotateY = ((x - centerX) / centerX) * 5; // Max 5 degrees

    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
  };

  const createRipple = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isCurrentPlan) return;

    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newRipple = {
      id: rippleIdRef.current++,
      x,
      y,
    };

    setRipples((prev) => [...prev, newRipple]);

    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);
  };

  return (
    <div
      ref={cardRef}
      className={`relative rounded-[8px] transition-all duration-200 ease-out group flex flex-col h-full ${sortOrder >= 1 ? 'z-10' : 'z-0'
        }`}
      style={{
        background: colorScheme.cardBg,
        boxShadow: sortOrder >= 1 ? colorScheme.shadow : '0 10px 30px -10px rgba(0,0,0,0.05), 0 0 1px 0 rgba(0,0,0,0.1)',
        border: `1.5px solid ${sortOrder >= 1 ? colorScheme.borderColor : '#E2E8F0'}`,
        transform: `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={createRipple}
      onClick={createRipple}
    >
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full pointer-events-none animate-ripple"
          style={{
            left: `${ripple.x}px`,
            top: `${ripple.y}px`,
            width: '0px',
            height: '0px',
            background: `radial-gradient(circle, ${colorScheme.accentColor}20, transparent)`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}

      {sortOrder === 1 && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
          <div
            className="text-white text-[11px] px-4 py-1.5 font-bold uppercase tracking-widest rounded-full shadow-lg"
            style={{
              background: colorScheme.accentGradient,
            }}
          >
            Most Popular
          </div>
        </div>
      )}

      {isCurrentPlan && (
        <div className="absolute top-4 right-4 z-20">
          <Badge className="bg-slate-100 text-slate-900 border-none px-3 py-1 font-semibold flex items-center gap-1.5">
            <CheckCircle className="h-3 w-3 text-emerald-500" />
            Current
          </Badge>
        </div>
      )}

      <div className="p-6 sm:p-8 pb-6 relative z-10 flex-1 flex flex-col">
        <div className="mb-6 sm:mb-8">
          <div
            className="w-10 h-10 sm:w-12 sm:h-12 mb-4 sm:mb-6 rounded-[8px] flex items-center justify-center"
            style={{ background: colorScheme.iconBg }}
          >
            <Zap className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: colorScheme.iconColor }} />
          </div>

          <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1.5 sm:mb-2 text-left">
            {plan.name || 'Plan'}
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 leading-relaxed min-h-[40px] text-left">
            {plan.description || `${plan.name} tailored for your business needs.`}
          </p>
        </div>

        <div className="mb-6 sm:mb-8 border-b border-slate-100 pb-6 sm:pb-8">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              ₹{isYearly ? Math.round(planPrice * 0.8) : planPrice}
            </span>
            <span className="text-slate-500 font-medium text-sm sm:text-base">/mo</span>
          </div>
          {isYearly && (
            <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 uppercase tracking-tight">
              SAVE 20% ANNUALLY
            </div>
          )}
        </div>

        <div className="space-y-4 sm:space-y-6 flex-1 mb-6 sm:mb-8">
          <p className="text-[10px] sm:text-xs font-bold text-slate-600 border-b border-slate-50 pb-2 uppercase tracking-wider text-left">Features & Limits</p>
          <ul className="space-y-2.5 sm:space-y-3">
            {(() => {
              const featuresObject = plan.features && typeof plan.features === 'object' ? plan.features : {};
              const allFeatures = getAllFeatures(featuresObject);

              return allFeatures.map((feature) => {
                let displayLimit = feature.limit;
                if (feature.key === 'team_management' && feature.subFeatures) {
                  const totalUsers = (feature.subFeatures.max_admins?.limit ?? 0) +
                    (feature.subFeatures.max_users?.limit ?? 0) +
                    (feature.subFeatures.max_agents?.limit ?? 0);
                  displayLimit = feature.limit === -1 ? -1 : (totalUsers > 0 ? totalUsers : feature.limit);
                }

                return (
                  <li key={feature.key} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-slate-50 flex items-center justify-center shrink-0">
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                    </div>
                    <div className="flex-1 flex justify-between items-center text-xs sm:text-sm">
                      <span className="text-slate-600 font-medium">{feature.name}</span>
                      <span className="font-bold text-slate-900">{formatLimit(displayLimit, feature.enabled)}</span>
                    </div>
                  </li>
                );
              });
            })()}
          </ul>
        </div>

        {(() => {
          const hasTrial = plan.trial_days && plan.trial_days > 0;
          const hasAnyGatewayPlanId = (plan.razorpay_plan_id && plan.razorpay_plan_id.trim() !== '') || ((plan as any).cashfree_plan_id && (plan as any).cashfree_plan_id.trim() !== '');
          const isTrialInvalid = hasTrial && !hasAnyGatewayPlanId;

          return (
            <button
              className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-[8px] font-bold text-xs sm:text-sm transition-all duration-300 ${isCurrentPlan || isTrialInvalid
                ? 'bg-slate-50 text-slate-400 cursor-not-allowed border border-slate-100'
                : 'hover:scale-[1.02] active:scale-[0.98]'
                }`}
              onClick={() => !isCurrentPlan && !isTrialInvalid && onSelectPlan(plan)}
              disabled={isCurrentPlan || isTrialInvalid}
              style={!isCurrentPlan && !isTrialInvalid ? {
                background: colorScheme.buttonBg,
                color: colorScheme.buttonTextColor,
                border: colorScheme.buttonBorder,
                boxShadow: sortOrder >= 1 ? `0 10px 20px -5px ${sortOrder === 1 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.3)'}` : 'none'
              } : undefined}
              onMouseEnter={(e) => {
                if (!isCurrentPlan && !isTrialInvalid && colorScheme.buttonHover) {
                  e.currentTarget.style.background = colorScheme.buttonHover;
                  if (sortOrder >= 1) e.currentTarget.style.color = '#FFFFFF';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCurrentPlan && !isTrialInvalid && colorScheme.buttonBg) {
                  e.currentTarget.style.background = colorScheme.buttonBg;
                  if (sortOrder >= 1) e.currentTarget.style.color = colorScheme.buttonTextColor;
                }
              }}
            >
              {isCurrentPlan
                ? 'Active'
                : isTrialInvalid
                  ? 'Trial Not Available'
                  : hasTrial
                    ? `Start ${plan.trial_days}-Day Trial`
                    : `Subscribe to ${plan.name}`}
            </button>
          );
        })()}
      </div>
    </div>
  );
};

interface BillingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BillingDialog: React.FC<BillingDialogProps> = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [step, setStep] = useState<'plans' | 'payment' | 'onboarding' | 'priorities'>('plans');
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [hasCompletedPayment, setHasCompletedPayment] = useState(false);
  const selectedPlanHasTrial = !!(selectedPlan && selectedPlan.trial_days && selectedPlan.trial_days > 0);

  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<'razorpay' | 'cashfree'>('razorpay');

  const [businessName, setBusinessName] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>('');

  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);

  const { data: plansData, isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const result = await subscriptionsApi.getPlans();
      if (result.error) {
        throw ApiError.fromResult(result);
      }
      const plans = result.data || [];
      return plans;
    },
    enabled: open,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (plans don't change frequently)
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: currentSubscription } = useCurrentSubscription({
    enabled: open,
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      if (!open) return false;
      const subscription = query.state.data as any;
      if (subscription?.status === 'pending') {
        return 10 * 1000;
      }
      return false;
    },
  });

  const isRenewal = isSubscriptionExpiredOrCancelled(currentSubscription);

  React.useEffect(() => {
    if (open && currentSubscription) {
      const isActiveOrTrialing = isSubscriptionActive(currentSubscription);

      const isPaymentDoneButPending =
        hasCompletedPayment &&
        currentSubscription.status === 'pending';

      if (isPaymentDoneButPending) {
        return;
      }

      if (isActiveOrTrialing) {
        onOpenChange(false);
      }
    }
  }, [open, currentSubscription, onOpenChange, hasCompletedPayment]);

  const isShopifyUser = currentSubscription?.user_type === 'shopify_store';

  const sortedPlans = React.useMemo(() => {
    if (!plansData || !Array.isArray(plansData)) return [];

    let plans = [...plansData];

    if (isShopifyUser) {
      plans = plans.filter(plan => {
        const normalizedType = ((plan as any).type || '').toLowerCase();
        const normalizedSlug = (plan.slug || '').toLowerCase();
        return normalizedType === 'shopify' || normalizedType === 'shopify_store' || normalizedSlug.includes('shopify');
      });
    } else {
      plans = plans.filter(plan => {
        const normalizedType = ((plan as any).type || '').toLowerCase();
        const normalizedSlug = (plan.slug || '').toLowerCase();
        return normalizedType !== 'shopify' && normalizedType !== 'shopify_store' && !normalizedSlug.includes('shopify');
      });
    }

    plans = plans.filter(plan => {
      const slug = (plan.slug || '').toLowerCase();
      const price = parseFloat(plan.price || '0');
      return !slug.includes('trial') && price > 1;
    });

    const sorted = plans.sort((a, b) => {
      const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return (a.id || '').localeCompare(b.id || '');
    });

    const filtered = sorted.filter(plan => {
      const isCurrentPlan = currentSubscription?.plan_id === plan.id;
      const isActiveSub = isSubscriptionActive(currentSubscription);
      if (isCurrentPlan && isActiveSub) {
        return false;
      }
      return true;
    });
    return filtered;
  }, [plansData, currentSubscription, isShopifyUser]);

  const planGridClass = React.useMemo(() => {
    const count = sortedPlans.length;
    if (count >= 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    if (count === 2) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2';
    return 'grid-cols-1 sm:grid-cols-1 lg:grid-cols-1';
  }, [sortedPlans.length]);

  const getProgress = () => {
    switch (step) {
      case 'plans': return 25;
      case 'payment': return 50;
      case 'onboarding': return 75;
      case 'priorities': return 100;
      default: return 0;
    }
  };

  const priorities = [
    {
      id: 'marketing-automation',
      label: 'Marketing Automation',
      description: 'Set up automated campaigns and workflows.',
      icon: Zap,
    },
    {
      id: 'customer-support',
      label: 'Customer Support',
      description: 'Manage customer conversations across channels.',
      icon: MessageSquare,
    },
    {
      id: 'sales-revenue',
      label: 'Sales & Revenue',
      description: 'Track and optimize sales performance.',
      icon: ShoppingCart,
    },
    {
      id: 'analytics-insights',
      label: 'Analytics & Insights',
      description: 'Get detailed reports and insights.',
      icon: TrendingUp,
    },
  ];

  const industries = [
    { id: 'real-estate', label: 'Real Estate', icon: FileText },
    { id: 'restaurant', label: 'Restaurant', icon: UtensilsCrossed },
    { id: 'agency', label: 'Agency', icon: Building },
    { id: 'course-creator', label: 'Course Creator', icon: GraduationCap },
    { id: 'coaching', label: 'Coaching/Consulting', icon: Handshake },
    { id: 'healthcare', label: 'Healthcare', icon: Heart },
    { id: 'education', label: 'Education', icon: Book },
    { id: 'fitness', label: 'Fitness & Wellness', icon: Dumbbell },
    { id: 'other', label: 'Other', icon: Building },
  ];

  const sources = [
    'Instagram',
    'LinkedIn',
    'Meta Partner Directory',
    'Shopify App Store',
    'Friend',
    'Agency',
    'YouTube',
    'Google',
    'Community',
    'Other',
  ];

  const handleRazorpayCheckout = async (checkoutData: any, subscriptionId?: string) => {
    try {
      await loadRazorpayScript();

      if (!window.Razorpay) {
        throw new Error('Razorpay checkout script failed to load. Please refresh the page and try again.');
      }

      const isSubscription = !!checkoutData.subscription_id;
      const isOrder = !!checkoutData.order_id;

      const razorpayOptions: any = {
        key: checkoutData.key,
        name: checkoutData.name || 'Subscription Payment',
        description: checkoutData.description || 'Complete your subscription payment',
        prefill: checkoutData.prefill || {},
        theme: {
          color: '#6366f1', // Primary color
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            toast({ variant: "success",
              title: 'Authentication Cancelled',
              description: 'You can complete the authentication later from the billing page.',
            });
          },
          backdropclose: true,
          escape: true,
          animation: true,
        },
        handler: async function (response: any) {
          /*
           * Tell the server, like useSubscriptionCheckout does. This handler
           * used to discard Razorpay's payment id + signature, leaving the
           * webhook as the only way the payment could activate the plan.
           */
          if (isSubscription && response?.razorpay_payment_id) {
            try {
              await subscriptionsApi.verifyPayment({
                razorpaySubscriptionId: response.razorpay_subscription_id || checkoutData.subscription_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
            } catch {
              /* the webhook and status reconciliation can still finish the job */
            }
          }
          setIsProcessing(false);
          setHasCompletedPayment(true);

          if (isSubscription && selectedPlanHasTrial) {
            toast({ variant: "success",
              title: 'Free Trial Started!',
              description: `Your ${selectedPlan?.trial_days}-day free trial has started. Your card has been validated but not charged. You will be charged after the trial period.`,
            });
          } else {
            toast({
              title: 'Payment Successful!',
              description: 'Payment completed. Waiting for confirmation. You can continue with onboarding.',
            });
          }

          if (isRenewal) {
            onOpenChange(false);
            // Reload once the server grants access, not on a timer.
            await awaitSubscriptionAccess();
            window.location.reload();
          } else {
            setStep('onboarding');
          }
        },
      };

      if (isSubscription) {
        razorpayOptions.subscription_id = checkoutData.subscription_id;
      }
      else if (isOrder) {
        razorpayOptions.order_id = checkoutData.order_id;
        razorpayOptions.amount = checkoutData.amount;
        razorpayOptions.currency = checkoutData.currency || 'INR';
        razorpayOptions.method = {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
          emi: false,
        };
        razorpayOptions.retry = {
          enabled: true,
          max_count: 3,
        };
      } else {
        throw new Error('Invalid checkout data: missing subscription_id or order_id');
      }

      const razorpay = new window.Razorpay(razorpayOptions);

      razorpay.open();

      setTimeout(() => {
        const razorpayIframe = document.querySelector('iframe[src*="razorpay"], iframe[src*="checkout"]');
        const razorpayModal = document.querySelector('.razorpay-container, [class*="razorpay"], [id*="razorpay"]');

        if (razorpayIframe) {
          const iframe = razorpayIframe as HTMLIFrameElement;
          iframe.style.pointerEvents = 'auto';
          iframe.style.zIndex = '999999';
        } else if (razorpayModal) {
          const modalElement = razorpayModal as HTMLElement;
          modalElement.style.pointerEvents = 'auto';
          modalElement.style.zIndex = '999999';
        }
      }, 1000);
    } catch (error) {
      setIsProcessing(false);
      showApiError(error, { fallback: { title: 'Error' } });
    }
  };

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    const hasTrial = plan.trial_days && plan.trial_days > 0;
    const hasRazorpayPlanId = plan.razorpay_plan_id && plan.razorpay_plan_id.trim() !== '';
    const hasCashfreePlanId = (plan as any).cashfree_plan_id && (plan as any).cashfree_plan_id.trim() !== '';
    const hasAnyGatewayPlanId = hasRazorpayPlanId || hasCashfreePlanId;

    if (hasTrial && !hasAnyGatewayPlanId) {
      toast({
        title: 'Configuration Error',
        description: `This plan has a ${plan.trial_days}-day free trial configured, but no payment gateway plan ID is set up. Please contact support.`,
        variant: 'destructive',
      });
      return;
    }

    setSelectedPlan(plan);
    setStep('payment');
    setHasCompletedPayment(false);
  };

  const handlePlanPayment = async () => {
    if (!selectedPlan) {
      toast({
        title: 'Select a plan',
        description: 'Please choose a plan before continuing.',
        variant: 'destructive',
      });
      return;
    }

    const hasTrial = selectedPlan.trial_days && selectedPlan.trial_days > 0;
    const hasRazorpayPlanId = selectedPlan.razorpay_plan_id && selectedPlan.razorpay_plan_id.trim() !== '';
    const hasCashfreePlanId = (selectedPlan as any).cashfree_plan_id && (selectedPlan as any).cashfree_plan_id.trim() !== '';

    if (hasTrial && !hasRazorpayPlanId && !hasCashfreePlanId) {
      toast({
        title: 'Configuration Error',
        description: `This plan has a ${selectedPlan.trial_days}-day free trial configured, but no payment gateway plan ID is set up. Please contact support.`,
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    try {
      const result = await subscriptionsApi.createCheckoutSession(selectedPlan.id, selectedGateway);
      if (result.error) {
        showApiError(result, { fallback: { title: 'Error' } });
        return;
      }

      if (result.data?.message && result.data.message.includes('Free plan activated')) {
        toast({ variant: "success",
          title: 'Success!',
          description: 'Free plan activated. You can now start using the platform.',
        });
        setHasCompletedPayment(true);
        if (isRenewal) {
          onOpenChange(false);
          setTimeout(() => window.location.reload(), 500);
        } else {
          setStep('onboarding');
        }
        return;
      }

      const planHasTrial = selectedPlan.trial_days && selectedPlan.trial_days > 0;
      const responseData = result.data;

      const checkoutData = responseData?.checkoutData;

      if (checkoutData?.gateway === 'cashfree' && checkoutData.payment_session_id) {
        setIsProcessing(false); // clear processing before closing
        onOpenChange(false);    // close Radix dialog → removes pointer-events block

        await new Promise(resolve => setTimeout(resolve, 150));

        await loadCashfreeScript();
        const cashfree = window.Cashfree({
          mode: checkoutData.environment === 'production' ? 'production' : 'sandbox',
        });

        let cfResult: any;
        try {
          cfResult = await cashfree.checkout({
            paymentSessionId: checkoutData.payment_session_id,
            redirectTarget: '_modal',
          });
        } catch (cfError: any) {
          toast({ variant: "success",
            title: 'Payment Cancelled',
            description: 'You closed the payment window. You can try again anytime.',
          });
          onOpenChange(true); // reopen billing dialog at payment step
          return;
        }

        if (cfResult?.error) {
          toast({
            title: 'Payment Failed',
            description: cfResult.error.message || 'Payment could not be completed. Please try again.',
            variant: 'destructive',
          });
          onOpenChange(true);
        } else if (cfResult?.paymentDetails) {
          try {
            console.log('Cashfree checkout paymentDetails:', JSON.stringify(cfResult.paymentDetails));
            await subscriptionsApi.verifyPayment({
              gateway: 'cashfree',
              cashfreeOrderId: checkoutData.order_id,
            });
          } catch { /* webhook will handle it */ }

          if (planHasTrial) {
            toast({ variant: "success",
              title: 'Free Trial Started!',
              description: `Your ${selectedPlan.trial_days}-day free trial has started. Your card has been validated but not charged. You will be charged after the trial period.`,
            });
          } else {
            toast({
              title: 'Payment Successful!',
              description: 'Payment completed. You can continue with onboarding.',
            });
          }

          setHasCompletedPayment(true);
          if (isRenewal) {
            onOpenChange(false);
            setTimeout(() => window.location.reload(), 500);
          } else {
            setStep('onboarding');
            onOpenChange(true);
          }
        } else {
          toast({ variant: "success",
            title: 'Payment Cancelled',
            description: 'You closed the payment window. You can try again anytime.',
          });
          onOpenChange(true); // reopen dialog at payment step
        }
      } else if (responseData?.razorpaySubscription?.id) {
        toast({ variant: "success",
          title: planHasTrial ? 'Starting Free Trial' : 'Complete Payment',
          description: planHasTrial
            ? `Please complete authentication to start your ${selectedPlan.trial_days}-day free trial. Your card will be validated but not charged.`
            : 'Please complete authentication to activate your subscription.',
        });

        const rpCheckoutData = {
          subscription_id: responseData.razorpaySubscription.id,
          key: responseData.key,
          name: selectedPlan.name,
          description: `Subscription to ${selectedPlan.name}`,
          prefill: {
            name: (user as any)?.name || '',
            email: user?.email || '',
            contact: (user as any)?.phoneNumber || '',
          },
        };

        await handleRazorpayCheckout(rpCheckoutData, responseData.subscriptionId);
      } else if (checkoutData?.subscription_id || checkoutData?.order_id) {
        toast({ variant: "success",
          title: planHasTrial ? 'Starting Free Trial' : 'Complete Payment',
          description: planHasTrial
            ? `Please complete authentication to start your ${selectedPlan.trial_days}-day free trial.`
            : 'Please complete the payment to start your subscription.',
        });
        await handleRazorpayCheckout(checkoutData, responseData.subscriptionId);
      } else {
        toast({ variant: "success",
          title: 'Payment Initiated',
          description: 'Your subscription is being processed.',
        });
      }
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Error' } });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOnboardingSubmit = async () => {
    if (!businessName || !selectedIndustry || !selectedSource) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setStep('priorities');
  };

  const handlePrioritiesToggle = (priorityId: string) => {
    setSelectedPriorities(prev =>
      prev.includes(priorityId)
        ? prev.filter(id => id !== priorityId)
        : [...prev, priorityId]
    );
  };

  const handleCompleteSetup = async () => {
    toast({ variant: "success",
      title: 'Setup Complete',
      description: 'Thanks! We are finalizing your workspace.',
    });
    onOpenChange(false);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const calculateTotal = () => {
    if (!selectedPlan) return 0;
    const price = parseFloat(selectedPlan.price || '0');
    return isYearly ? Math.round(price * 0.8 * 12) : price;
  };

  const planPrice = selectedPlan ? parseFloat(selectedPlan.price || '0') : 0;
  const monthlyPrice = isYearly ? Math.round(planPrice * 0.8) : planPrice;
  const yearlyTotal = isYearly ? Math.round(planPrice * 0.8 * 12) : planPrice * 12;

  const getStepTitle = () => {
    switch (step) {
      case 'plans': return 'Choose Your Plan';
      case 'payment': return 'Payment Details & Billing Summary';
      case 'onboarding': return "Let's Get to Know Your Brand";
      case 'priorities': return 'What are your main priorities?';
      default: return '';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'plans': return 'Select a subscription plan to get started with wenext';
      case 'payment': return 'Complete your payment to activate your subscription';
      case 'onboarding': return "We'll tailor your wenext experience to match your unique needs.";
      case 'priorities': return 'Select all that apply to customize your dashboard.';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] sm:w-[90vw] max-h-[90vh] flex flex-col p-0 [&>button.absolute]:hidden">
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <Progress value={getProgress()} className="h-2" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl sm:text-2xl font-semibold text-[#08090A]">
              {getStepTitle()}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {getStepDescription()}
            </DialogDescription>
          </DialogHeader>

          {step === 'plans' ? (
            <div className="space-y-5">
              <div className="flex justify-center">
                <div className="flex items-center gap-2 p-2 h-[50px] bg-[#F5F7F9] rounded-[20px]">
                  <button
                    onClick={() => setIsYearly(false)}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-[12px] text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${!isYearly ? 'bg-white text-[#29292D] shadow-sm' : 'text-muted-foreground'
                      }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setIsYearly(true)}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-[12px] text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${isYearly ? 'bg-white text-[#29292D] shadow-sm' : 'text-muted-foreground'
                      }`}
                  >
                    Yearly
                    <Badge className="ml-1 bg-green-50 text-green-700 border border-green-200 text-[10px] px-1.5 py-0">Save 20%</Badge>
                  </button>
                </div>
              </div>

              {plansLoading ? (
                <div className="flex items-center justify-center py-12 sm:py-16">
                  <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary" />
                </div>
              ) : plansError ? (
                <Card className="border-none shadow-[0_0_10px_0_rgba(0,0,0,0.06)] rounded-[12px] bg-white">
                  <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
                    <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-[#29292D]">Error Loading Plans</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-4 text-center px-4">
                      {friendlyMessage(plansError, "We couldn't load the plans. Please try again.")}
                    </p>
                    <Button onClick={() => window.location.reload()} size="sm" className="text-xs sm:text-sm">
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : !sortedPlans || sortedPlans.length === 0 ? (
                <Card className="border-none shadow-[0_0_10px_0_rgba(0,0,0,0.06)] rounded-[12px] bg-white">
                  <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-[#29292D]">No Plans Available</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground text-center px-4">
                      No subscription plans are currently available. Please contact support.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className={`grid ${planGridClass} gap-3 sm:gap-4 lg:gap-5`}>
                  {sortedPlans.map((plan, index) => {
                    const isCurrentPlan = currentSubscription?.plan_id === plan.id;
                    const isActiveSub = isSubscriptionActive(currentSubscription);
                    const planFeatures = formatPlanFeatures(plan.features);
                    const planPrice = parseFloat(plan.price || '0');
                    const sortOrder = plan.sort_order ?? 0;
                    const colorScheme = getPlanColorScheme(sortOrder);
                    const hasTrial = plan.trial_days && plan.trial_days > 0;

                    if (isCurrentPlan && isActiveSub)
                      return null;

                    return (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        isCurrentPlan={isCurrentPlan && isActiveSub}
                        planFeatures={planFeatures}
                        planPrice={planPrice}
                        sortOrder={sortOrder}
                        colorScheme={colorScheme}
                        isYearly={isYearly}
                        onSelectPlan={handlePlanSelect}
                        isFirstTwo={hasTrial}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ) : step === 'payment' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Button variant="ghost" size="sm" onClick={() => setStep('plans')} className="p-2">
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h3 className="text-lg font-semibold text-[#08090A]">Confirm & Pay</h3>
                  </div>
                  {!selectedPlan ? (
                    <p className="text-sm text-muted-foreground">
                      Select a plan to continue to payment.
                    </p>
                  ) : hasCompletedPayment ? (
                    <div className="flex items-start gap-2 text-sm text-emerald-700">
                      <CheckCircle className="h-4 w-4 mt-0.5" />
                      <div>
                        <p className="font-semibold">Payment confirmed</p>
                        <p>Great! Let’s capture a few more onboarding details.</p>
                      </div>
                    </div>
                  ) : selectedPlanHasTrial ? (
                    <p className="text-sm text-muted-foreground">
                      You’ll authorize a payment method. No charge until your {selectedPlan.trial_days}‑day trial ends.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      This plan doesn’t include a free trial. Your payment will be collected immediately.
                    </p>
                  )}
                </div>
                {!hasCompletedPayment && selectedPlan && (
                  <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
                    <p className="text-sm font-semibold text-foreground mb-3">Select Payment Method</p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedGateway('razorpay')}
                        className={`flex-1 flex items-center gap-3 rounded-lg border-2 p-3 transition-all ${
                          selectedGateway === 'razorpay'
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-border/50 bg-background hover:border-border'
                        }`}
                      >
                        <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                          selectedGateway === 'razorpay' ? 'border-primary' : 'border-muted-foreground/40'
                        }`}>
                          {selectedGateway === 'razorpay' && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-foreground">Razorpay</p>
                          <p className="text-xs text-muted-foreground">Cards, UPI, Net Banking</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedGateway('cashfree')}
                        className={`flex-1 flex items-center gap-3 rounded-lg border-2 p-3 transition-all ${
                          selectedGateway === 'cashfree'
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-border/50 bg-background hover:border-border'
                        }`}
                      >
                        <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                          selectedGateway === 'cashfree' ? 'border-primary' : 'border-muted-foreground/40'
                        }`}>
                          {selectedGateway === 'cashfree' && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-foreground">Cashfree</p>
                          <p className="text-xs text-muted-foreground">Cards, UPI, Net Banking</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
                {!hasCompletedPayment && (
                  <div className="rounded-lg border border-border/40 bg-background p-4 text-sm text-muted-foreground">
                    <p className="font-semibold text-foreground mb-1">What happens next?</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>We’ll open a secure checkout to collect payment.</li>
                      <li>Once payment succeeds you can continue onboarding.</li>
                      <li>If payment stays pending, the top bar will show “Payment Pending”.</li>
                    </ul>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#08090A]">Billing Summary</h3>

                {selectedPlan && (
                  <Card className="border border-border/50">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-semibold">{selectedPlan.name}</CardTitle>
                        {selectedPlanHasTrial && (
                          <Badge variant="secondary">Free Trial</Badge>
                        )}
                      </div>
                      <CardDescription className="text-xs mt-1">
                        {selectedPlan.description || `${selectedPlan.name} plan`}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-muted-foreground uppercase">Plan Includes</div>
                        <div className="space-y-1.5 text-xs">
                          {(() => {
                            const features = selectedPlan.features && typeof selectedPlan.features === 'object' ? selectedPlan.features : {};
                            const allFeatures = getAllFeatures(features);

                            if (allFeatures.length === 0) {
                              return (
                                <>
                                  <div className="flex items-center justify-between py-1.5">
                                    <span className="text-muted-foreground">Contacts</span>
                                    <span className="text-xs font-semibold text-foreground">
                                      {formatLimit(selectedPlan.features?.contacts?.limit ?? null, true)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1.5">
                                    <span className="text-muted-foreground">Messages</span>
                                    <span className="text-xs font-semibold text-foreground">
                                      {formatLimit(selectedPlan.features?.messages_per_month?.limit ?? null, true)}
                                    </span>
                                  </div>
                                </>
                              );
                            }

                            return (
                              <>
                                {allFeatures.map((feature) => {
                                  if (feature.key === 'team_management' && feature.subFeatures) {
                                    const totalUsers = (feature.subFeatures.max_admins?.limit ?? 0) +
                                      (feature.subFeatures.max_users?.limit ?? 0) +
                                      (feature.subFeatures.max_agents?.limit ?? 0);
                                    const displayLimit = feature.limit === -1 ? -1 : (totalUsers > 0 ? totalUsers : feature.limit);
                                    return (
                                      <div key={feature.key} className="flex items-center justify-between py-1.5">
                                        <span className="text-muted-foreground">{feature.name}</span>
                                        <span className={`text-xs font-semibold ${feature.enabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                                          {formatLimit(displayLimit, feature.enabled)}
                                        </span>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div key={feature.key} className="flex items-center justify-between py-1.5">
                                      <span className="text-muted-foreground">{feature.name}</span>
                                      <span className={`text-xs font-semibold ${feature.enabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {formatLimit(feature.limit, feature.enabled)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="border-t border-border/50 pt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Plan Price</span>
                          <span className="font-medium">₹{monthlyPrice}/month</span>
                        </div>
                        {isYearly && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Billing Period</span>
                            <span className="font-medium">Annual (Save 20%)</span>
                          </div>
                        )}
                        {selectedPlanHasTrial ? (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Free Trial</span>
                            <span className="font-medium text-green-600">{selectedPlan?.trial_days} days</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-xs text-muted-foreground">
                            Card details will be collected securely. No trial for this plan.
                          </div>
                        )}
                      </div>

                      <div className="border-t border-border/50 pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-base font-semibold">Total</span>
                          <div className="text-right">
                            <div className="text-lg font-bold text-[#08090A]">
                              ₹{isYearly ? yearlyTotal : monthlyPrice}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {isYearly ? 'per year' : 'per month'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          ) : step === 'onboarding' ? (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep('payment')}
                  className="p-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <h3 className="text-lg font-semibold text-[#08090A]">Business Information</h3>
              </div>

              <form className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="businessName">Business Name</Label>
                  <Input
                    id="businessName"
                    placeholder="Store Name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessWebsite">Business Website</Label>
                  <div className="flex items-center">
                    <div className="px-3 py-2 bg-muted border border-r-0 border-input rounded-l-md text-sm text-muted-foreground">
                      https://
                    </div>
                    <Input
                      id="businessWebsite"
                      placeholder="store.com"
                      value={businessWebsite}
                      onChange={(e) => setBusinessWebsite(e.target.value)}
                      className="rounded-l-none"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>What industry does your business belong to?</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                    {industries.map((industry) => {
                      const Icon = industry.icon;
                      const isSelected = selectedIndustry === industry.id;
                      return (
                        <button
                          key={industry.id}
                          type="button"
                          onClick={() => setSelectedIndustry(industry.id)}
                          className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                            }`}
                        >
                          <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-xs font-medium text-center ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                            {industry.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>How did you hear about wenext?</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {sources.map((source) => {
                      const isSelected = selectedSource === source;
                      return (
                        <button
                          key={source}
                          type="button"
                          onClick={() => setSelectedSource(source)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium ${isSelected
                            ? 'border-[#08090A] bg-[#08090A] text-[#fff]'
                            : 'border-border hover:border-primary/50 text-muted-foreground'
                            }`}
                        >
                          {source}
                        </button>
                      );
                    })}
                  </div>
                </div>

              </form>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {priorities.map((priority) => {
                  const Icon = priority.icon;
                  const isSelected = selectedPriorities.includes(priority.id);
                  return (
                    <button
                      key={priority.id}
                      type="button"
                      onClick={() => handlePrioritiesToggle(priority.id)}
                      className={`relative p-6 rounded-lg border-2 transition-all text-left ${isSelected
                        ? 'border-green-500 bg-green-50'
                        : 'border-border hover:border-primary/50 bg-white'
                        }`}
                    >
                      {isSelected && (
                        <div className="absolute top-4 right-4">
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <CheckCircle className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${isSelected ? 'bg-green-100' : 'bg-muted'
                          }`}>
                          <Icon className={`h-6 w-6 ${isSelected ? 'text-green-600' : 'text-muted-foreground'
                            }`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-semibold mb-1 ${isSelected ? 'text-green-700' : 'text-[#08090A]'
                            }`}>
                            {priority.label}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {priority.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedPriorities.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-800 font-medium">
                      You're all set! We've customized your experience based on your selections. You can always change these settings later.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-background">
          <div className="flex justify-between items-center">
            {step === 'plans' ? (
              <div></div>
            ) : step === 'payment' ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setStep('plans')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={hasCompletedPayment
                    ? (isRenewal
                      ? () => { onOpenChange(false); setTimeout(() => window.location.reload(), 500); }
                      : () => setStep('onboarding'))
                    : handlePlanPayment}
                  disabled={isProcessing || (!selectedPlan && !hasCompletedPayment)}
                  style={selectedPlan && getPlanColorScheme(selectedPlan.sort_order ?? 0).buttonBg ? {
                    background: getPlanColorScheme(selectedPlan.sort_order ?? 0).buttonBg,
                  } : undefined}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : hasCompletedPayment ? (
                    isRenewal ? 'Done' : 'Continue'
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      {selectedPlanHasTrial ? 'Start Free Trial' : 'Proceed to Payment'}
                    </>
                  )}
                </Button>
              </>
            ) : step === 'onboarding' ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setStep('payment')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={handleOnboardingSubmit}
                  disabled={isProcessing || !businessName || !selectedIndustry || !selectedSource}
                  style={selectedPlan && getPlanColorScheme(selectedPlan.sort_order ?? 0).buttonBg ? {
                    background: getPlanColorScheme(selectedPlan.sort_order ?? 0).buttonBg,
                  } : undefined}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Next'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setStep('onboarding')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={handleCompleteSetup}
                  disabled={isProcessing}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Complete Setup'
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BillingDialog;
